import React, { useMemo } from 'react';
import { useCommodities } from './useMarketData'; // Verify path
import { COMMODITY_GROUPS } from '../data/commodityDefinitions';

// Reusing your existing DataRow style for consistency
const DataRow = ({ label, subLabel, price, change, isPositive, theme }) => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    padding: '0.75rem',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.05)',
    marginBottom: '0.5rem'
  }}>
    <div>
      <span style={{ fontWeight: 600, color: theme.textPrimary, fontSize: '0.9rem', display: 'block' }}>
        {label}
      </span>
      {/* Show Ticker as sublabel */}
      <span style={{ fontSize: '0.7rem', color: theme.textSecondary }}>
        {subLabel}
      </span>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: theme.textPrimary }}>
        {price}
      </div>
      <div style={{ 
        fontSize: '0.75rem', 
        fontWeight: 600, 
        color: isPositive ? '#4ade80' : '#ef4444' 
      }}>
        {change}
      </div>
    </div>
  </div>
);

// Generic Card for a Group
const GroupCard = ({ title, items, theme, limit = 6 }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '12px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)',
    height: '100%'
  }}>
    <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37', marginBottom: '1rem' }}>
      {title}
    </h3>
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {items.slice(0, limit).map((item, i) => (
        <DataRow 
          key={i}
          theme={theme}
          label={item.name} // Display Name (e.g., "Ouro")
          subLabel={item.symbol} // Display Ticker (e.g., "GC=F")
          price={`$${item.last_quote?.toFixed(2) || item.close?.toFixed(2) || '0.00'}`}
          change={`${item.rate >= 0 ? '+' : ''}${item.rate?.toFixed(2)}%`}
          isPositive={item.rate >= 0}
        />
      ))}
      {items.length === 0 && (
        <span style={{ color: theme.textSecondary, fontSize: '0.8rem', padding: '1rem', textAlign: 'center' }}>
          Sem dados (Verificar API)
        </span>
      )}
    </div>
  </div>
);

export const CommodityClusters = ({ theme }) => {
  const { data, loading } = useCommodities();

  // The logic to filter and sort
  const groups = useMemo(() => {
    if (!data) return {};

    const filterAndSort = (validTickers) => {
      return data
        // --- CRITICAL FIX: Check if the ITEM SYMBOL is in our list ---
        .filter(item => validTickers.includes(item.symbol))
        .sort((a, b) => Math.abs(b.rate) - Math.abs(a.rate)); // Sort by volatility
    };

    return {
      energy: filterAndSort(COMMODITY_GROUPS.ENERGY),
      metals: filterAndSort(COMMODITY_GROUPS.METALS),
      vegetable: filterAndSort(COMMODITY_GROUPS.VEGETABLE),
      industrial: filterAndSort(COMMODITY_GROUPS.INDUSTRIAL),
    };
  }, [data]);

  if (loading) return <div style={{ height: '200px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }} />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
      <GroupCard title="⚡ Energia" items={groups.energy || []} theme={theme} />
      <GroupCard title="🌾 Agrícola (Vegetal)" items={groups.vegetable || []} theme={theme} />
      <GroupCard title="🏭 Metais & Ind." items={groups.metals || []} theme={theme} />
    </div>
  );
};